import { Fragment,useMemo, useCallback, useState, useEffect } from 'react';
// Models
import { IPage } from '@root/models/Page';
// Selectors
import { selectAllPages, selectPageEntities, selectFilteredDocumentPages } from 'app/store/page/page.selectors';
import { selectDuplicatesList } from 'app/store/Cases/Cases.selectors';
// Lab
import { Box, Button, FormHelperText, TextField } from '@mui/material';
// Utilities
import { sortByStapleOrder } from '@root/utilities/sort';
import { selectSearchPagesList } from 'app/store/DMSDocumentPages/DMSDocumentPages.selectors';
// i18next
import { useTranslation } from 'react-i18next';
import { RootState } from 'app/store';
import { useAppSelector } from 'app/hooks/useStore';
import { selectSortedDocumentIds } from 'app/store/DMSBatches/DMSBatches.selectors';

type Props = {
  isFilesPage: boolean;
  isWorkspacePage: boolean;
  isSearchPage: boolean;
  isDuplicatePage: boolean;
  page: IPage;
  workspacePages: IPage[];
  prevDocumentId: number;
  onFetchPage: (documentId:number, pageNum:number, prevDocumentId?:number, nextTargetPageNum?:number|null) => void;
  targetPageNum?: number | null;
};

const CardPageNavigation = ({
  // Props
  isFilesPage, isWorkspacePage, isSearchPage, isDuplicatePage, page, workspacePages, prevDocumentId, onFetchPage, targetPageNum
}:Props) => {
  const { t } = useTranslation();
  // Selectors
  const sortedDocumentIds = useAppSelector(selectSortedDocumentIds);
  // State
  const pageEntities:Record<number, Record<number, IPage>> = useAppSelector(selectPageEntities);
  const allPages:Array<IPage> | null = useAppSelector(selectAllPages);
  const documentPages:Array<IPage> = useAppSelector((state:RootState) => selectFilteredDocumentPages(state, { documentId: page.documentId }));
  const duplicates = useAppSelector(selectDuplicatesList);
  const searchPages = useAppSelector(selectSearchPagesList);

  const duplicatePages = useMemo(() => {
    if ( !duplicates ) return [];
    return Object.values(duplicates).reduce((acc, cur) => {
      if ( cur.some((p) => p.documentId === page.documentId && p.pageNum === page.pageNum) ){
        acc = cur;
      }
      return acc;
    }, []);
  }, [duplicates, page]);

  const pages = useMemo(() => {
    if ( isSearchPage ) return searchPages;
    if ( isDuplicatePage ) return duplicatePages;
    if ( isFilesPage ) return documentPages;
    if ( isWorkspacePage ) return workspacePages;
    return [];
  }, [isSearchPage, isDuplicatePage, isFilesPage, isWorkspacePage, searchPages, duplicatePages, documentPages, workspacePages]);

  const pageIndex = useMemo(() => {
    return (pages || []).findIndex((p:IPage) => p.documentId === page.documentId && p.pageNum === (targetPageNum ? targetPageNum : page.pageNum));
  }, [pages, page, targetPageNum]);

  const getFilesNextPage = useCallback((isNext:boolean, fromDocumentId:number, fromPageNum:number) => {
    const documentIds = sortedDocumentIds || [];
    const currentDocIndex = documentIds.indexOf(fromDocumentId);
    if (currentDocIndex === -1) return null;

    const currentPages = pageEntities[fromDocumentId];
    if (!currentPages) return null;

    const pageNums = Object.keys(currentPages).map(Number).sort((a, b) => a - b);
    const currentPageIndex = pageNums.indexOf(fromPageNum);
    if (currentPageIndex === -1) return null;

    const nextPageIndex = currentPageIndex + (isNext ? 1 : -1);

    // Move to next/prev page within the same document
    if (nextPageIndex >= 0 && nextPageIndex < pageNums.length) {
      return currentPages[pageNums[nextPageIndex]];
    }

    // Move to first/last page of next/prev document
    const nextDocIndex = currentDocIndex + (isNext ? 1 : -1);
    if (nextDocIndex < 0 || nextDocIndex >= documentIds.length) return null;

    const nextDocId = documentIds[nextDocIndex];
    const nextDocPages = pageEntities[nextDocId];
    if (!nextDocPages) return null;
    const nextDocumentPagesValues = Object.values(nextDocPages);
    if (!nextDocumentPagesValues.length) return null;

    return nextDocumentPagesValues[isNext ? 0 : nextDocumentPagesValues.length - 1]
  },[sortedDocumentIds, pageEntities])

  const checkIsNextPageDisabled = useCallback((isNext:boolean, cb?:any) => {
    const nextPageIndex = isNext ? pageIndex + 1 : pageIndex - 1;
    let nextPage = (pages || [])[nextPageIndex];

    if ( !nextPage ){
      if ( !isFilesPage ) return true;
      nextPage = getFilesNextPage(isNext, page.documentId, page.pageNum);
      if ( !nextPage ) return true;
    }

    let nextDocumentId = nextPage.documentId;
    let nextPageNum = nextPage.pageNum;
    let nextTargetPageNum = null;

    if ( isSearchPage && nextPage ){
      const pageEntity = pageEntities[nextDocumentId][nextPageNum];
      if ( pageEntity && pageEntity.staple && pageEntity.staple.order > 1 ){
        const stapleId = pageEntity.staple.id;
        if ( allPages ){
          const staplePages = allPages.filter((p:IPage) => p.staple && p.staple.id === stapleId).sort(sortByStapleOrder);
          const mainStaplePage = staplePages[0];
          if ( mainStaplePage ){
            nextDocumentId = mainStaplePage.documentId;
            nextPageNum = mainStaplePage.pageNum;
            nextTargetPageNum = nextPage.pageNum;
          }
        }
      }
    }

    const isSecondaryNextPage = nextPage.staple && nextPage.staple.order > 1;

    if ( !isSecondaryNextPage ){
      if ( cb ) cb(nextDocumentId, nextPageNum, page.documentId, nextTargetPageNum);
      return false;
    }

    while ( !(!nextPage.staple || nextPage.staple.order === 1) ){
      nextPage = getFilesNextPage(isNext, nextDocumentId, nextPageNum);
      if ( !nextPage ) return true;
      nextDocumentId = nextPage.documentId;
      nextPageNum = nextPage.pageNum;
    }

    if ( cb ) cb(nextDocumentId, nextPageNum, page.documentId, nextTargetPageNum);
    return false;
  }, [isFilesPage, pageIndex, getFilesNextPage, page, pages, isSearchPage, pageEntities, allPages]);

  const [ goTo, setGoTo ] = useState<number | string>(page.pageNum);

  useEffect(() => {
    const handleKeydown = (event:any) => {
      const activeElement = document.activeElement;
      const tags = ['input', 'select', 'button', 'textarea'];
      const classes = ['ql-editor'];
      const inputmode = ['numeric', 'text'];
  
      const hasTags = tags.includes(activeElement?.tagName.toLowerCase() || '');
      const hasClasses = classes.includes(activeElement?.className || '');
      const hasInputmode = inputmode.includes(activeElement?.getAttribute('inputmode') || '');
  
      if ( hasTags || hasClasses || hasInputmode ) return;
  
      // Arrow left
      if ( event.keyCode === 37 && !checkIsNextPageDisabled(false) ) handlePageChange(false);
      // Arrow right
      if ( event.keyCode === 39 && !checkIsNextPageDisabled(true) ) handlePageChange(true);
    }

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
    // eslint-disable-next-line
  }, []);

  const isPrevPageDisabled = checkIsNextPageDisabled(false);
  const isNextPageDisabled = checkIsNextPageDisabled(true);

  const handlePageChange = (isNext:boolean) => {
    checkIsNextPageDisabled(isNext, (nextDocumentId:any, nextPageNum:any, documentId:any, nextTargetPageNum:any) => {
      onFetchPage(nextDocumentId, nextPageNum, documentId, nextTargetPageNum)
    })
  }

  const handleSubmit = (event:React.FormEvent) => {
    event.preventDefault();
    onFetchPage(page.documentId, Number(goTo));
  }

  return (
    <Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormHelperText>{t('dialogs.cardPage.stapledPagesSkipped')}</FormHelperText>
        <Button
          disabled={isPrevPageDisabled}
          onClick={() => handlePageChange(false)}
          color="primary"
          variant="outlined"
        >{t('buttons.prev')}</Button>
        <Button
          disabled={isNextPageDisabled}
          onClick={() => handlePageChange(true)}
          color="primary"
          variant="outlined"
        >{t('buttons.next')}</Button>
        {isFilesPage ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center'
            }}
            onSubmit={handleSubmit}
            component="form"
          >
            <TextField
              aria-label="Go to input"
              sx={{
                width: 142
              }}
              value={goTo}
              onChange={(event:React.ChangeEvent<HTMLInputElement>) => {
                const re = /^[0-9\b]+$/;
                if (event.target.value === '' || re.test(event.target.value)) {
                  setGoTo(event.target.value)
                }
              }}
              margin="none"
              size="small"
              slotProps={{
                input: {
                  sx: {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    pr: 0,
                    height: 36.5
                  },
                  endAdornment: (
                    <FormHelperText
                      sx={{ p: 0, mt: 0, fontSize: 16, whiteSpace: 'nowrap' }}
                    >{`/ ${documentPages?.length || 0}`}</FormHelperText>
                  )
                }
              }}
            />
            <Button
              sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, whiteSpace: 'nowrap' }}
              type="submit"
              disabled={typeof documentPages[Number(goTo) - 1] === 'undefined'}
              color="primary"
              variant="contained"
            >{t('buttons.goToPage')}</Button>
          </Box>
        ) : (
          <FormHelperText sx={{ mt: 0 }}>
            {t('dialogs.cardPage.pageOfPages', { currentPage: pageIndex + 1, allPages: (pages || []).length })}
          </FormHelperText>
        )}
      </Box>
    </Fragment>
  )
}

export default CardPageNavigation
